import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Space, Spin, Typography } from 'antd'
import { EnterpriseSummary, EnterpriseSupplementModal } from '@/features/service-enterprise/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'

export function OrderEnterprisePanel({
  subjectId,
  subjectName,
  currentOrder,
  canSupplement = false,
  disabled = false,
  actions,
  onPendingChange,
  onSuccess
}: {
  subjectId: number | null
  subjectName: string | null
  currentOrder: { type: 'rental' | 'service'; id: number }
  actions?: ReactNode
  canSupplement?: boolean
  disabled?: boolean
  onPendingChange?: (pending: boolean) => void
  onSuccess?: () => void
}) {
  const [open, setOpen] = useState(false)
  const changeOpen = (next: boolean) => {
    setOpen(next)
    onPendingChange?.(next)
  }
  const trpc = useTRPC()
  const access = useAccess()
  const detail = useQuery({
    ...trpc.order.subjects.detail.queryOptions({ subjectId: subjectId ?? 0 }),
    enabled: Boolean(subjectId) && access.can('subject.read')
  })
  if (!subjectId) {
    return (
      <Alert
        showIcon
        type="info"
        title="尚未关联服务对象"
        description="无合同且可编辑的订单，可在编辑订单中补关联服务对象。"
      />
    )
  }
  const enterprise = detail.data
  return (
    <div className="mt-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Space wrap>
          <Typography.Text strong>{enterprise?.name ?? subjectName ?? `服务对象 #${subjectId}`}</Typography.Text>
          <Typography.Text type="secondary">ID {subjectId}</Typography.Text>
          {detail.isFetching && <Spin size="small" />}
        </Space>
        <Space wrap>
          {canSupplement && access.can('subject.material.update') && (
            <Button
              type="primary"
              disabled={disabled || detail.isError || !enterprise}
              onClick={() => changeOpen(true)}
            >
              补充资料
            </Button>
          )}
          {actions}
        </Space>
      </div>
      {detail.isError && (
        <Alert
          showIcon
          type="error"
          title={detail.error.message}
          action={<Button onClick={() => void detail.refetch()}>重试</Button>}
        />
      )}
      {enterprise?.kind === 'person' && (
        <Alert
          showIcon
          type="warning"
          title="历史个人对象"
          description="保留原关联，请人工核对后再办理企业资料补录。"
        />
      )}
      {enterprise && (
        <>
          <EnterpriseSummary enterprise={enterprise} />
          <Typography.Text type="secondary">
            {actions
              ? '补充资料用于完善企业档案；修改资料用于调整本单服务对象及资料，保存订单后生效。历史合同与账单保留原资料。'
              : '此处展示企业当前资料。补充资料用于完善企业档案，历史订单、合同与账单保留原资料。'}
          </Typography.Text>
        </>
      )}
      {open && (
        <EnterpriseSupplementModal
          subjectId={subjectId}
          open={open}
          onOpenChange={changeOpen}
          currentOrder={currentOrder}
          onSuccess={() => {
            void detail.refetch()
            onSuccess?.()
          }}
        />
      )}
    </div>
  )
}
